import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { InsiderFiling } from "../api/types";

function fmtShares(n: number | null): string {
  if (n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtUsd(v: number | null): string {
  if (v === null) return "—";
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

const TXN_LABEL: Record<string, string> = {
  P: "Buy",
  S: "Sell",
  A: "Award",
  G: "Gift",
  M: "Exercise",
  D: "Disposition",
};

export default function InsiderActivityCard({ yahoo }: { yahoo: string }) {
  const q = useQuery<InsiderFiling[]>({
    queryKey: ["insider", yahoo],
    queryFn: async () =>
      (await api.get<InsiderFiling[]>(`/symbols/${encodeURIComponent(yahoo)}/insider`, {
        params: { limit: 10 },
      })).data,
    enabled: !!yahoo,
    staleTime: 5 * 60_000,
  });

  const rows = q.data ?? [];
  if (q.isLoading || rows.length === 0) return null;

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <h3 className="mb-3 text-sm font-medium text-slate-200">Insider activity</h3>
      <ul className="divide-y divide-slate-800">
        {rows.map((f, i) => {
          const tCls =
            f.transaction_type === "P"
              ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
              : f.transaction_type === "S"
              ? "bg-rose-500/15 text-rose-300 ring-rose-500/30"
              : "bg-slate-500/15 text-slate-400 ring-slate-500/30";
          const label = TXN_LABEL[f.transaction_type ?? ""] ?? f.transaction_type ?? "—";
          return (
            <li key={i} className="flex items-center gap-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${tCls}`}
                  >
                    {label}
                  </span>
                  <span className="truncate text-slate-200">{f.filer_name ?? "—"}</span>
                </div>
                <div className="truncate text-[11px] text-slate-500">
                  {f.filer_role ?? "—"} · {f.filing_date}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm tabular-nums text-slate-200">{fmtShares(f.shares)}</div>
                <div className="text-[11px] tabular-nums text-slate-500">{fmtUsd(f.value_usd)}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
